import { transFormOccupancy } from "../common/common.js";

const QUERY_KEYS = [
  "fico",
  "loanAmount",
  "state",
  "county",
  "housePrice",
  "purpose",
  "occupancy",
  "monthlyIncome",
  "monthlyDebt",
];

const STATE_ABBREV_TO_FULL = {
  AL: "ALABAMA",
  AK: "ALASKA",
  AZ: "ARIZONA",
  AR: "ARKANSAS",
  CA: "CALIFORNIA",
  CO: "COLORADO",
  CT: "CONNECTICUT",
  DE: "DELAWARE",
  FL: "FLORIDA",
  GA: "GEORGIA",
  HI: "HAWAII",
  ID: "IDAHO",
  IL: "ILLINOIS",
  IN: "INDIANA",
  IA: "IOWA",
  KS: "KANSAS",
  KY: "KENTUCKY",
  LA: "LOUISIANA",
  ME: "MAINE",
  MD: "MARYLAND",
  MA: "MASSACHUSETTS",
  MI: "MICHIGAN",
  MN: "MINNESOTA",
  MS: "MISSISSIPPI",
  MO: "MISSOURI",
  MT: "MONTANA",
  NE: "NEBRASKA",
  NV: "NEVADA",
  NH: "NEW HAMPSHIRE",
  NJ: "NEW JERSEY",
  NM: "NEW MEXICO",
  NY: "NEW YORK",
  NC: "NORTH CAROLINA",
  ND: "NORTH DAKOTA",
  OH: "OHIO",
  OK: "OKLAHOMA",
  OR: "OREGON",
  PA: "PENNSYLVANIA",
  RI: "RHODE ISLAND",
  SC: "SOUTH CAROLINA",
  SD: "SOUTH DAKOTA",
  TN: "TENNESSEE",
  TX: "TEXAS",
  UT: "UTAH",
  VT: "VERMONT",
  VA: "VIRGINIA",
  WA: "WASHINGTON",
  WV: "WEST VIRGINIA",
  WI: "WISCONSIN",
  WY: "WYOMING",
};

const VALID_PURPOSES = new Set(["Purchase", "RateTermRefi", "CashOut"]);

function queryHasKey(query, key) {
  if (!query.has(key)) return false;
  const value = query.get(key);
  return value != null && value !== "";
}

function parseIntOrNull(value, { min, max } = {}) {
  if (value == null || value === "") return null;
  const n = Number.parseInt(String(value).replace(/,/g, ""), 10);
  if (!Number.isFinite(n)) return null;
  if (min != null && n < min) return null;
  if (max != null && n > max) return null;
  return n;
}

export function normalizeState(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const upper = trimmed.toUpperCase();
  if (upper.length === 2 && STATE_ABBREV_TO_FULL[upper]) {
    return STATE_ABBREV_TO_FULL[upper];
  }
  return upper;
}

export function normalizeCounty(raw) {
  if (!raw) return null;
  const county = String(raw).trim().toUpperCase();
  if (!county) return null;
  if (/\b(COUNTY|PARISH)$/i.test(county)) {
    return county;
  }
  return `${county} COUNTY`;
}

export function normalizePurpose(raw) {
  if (!raw) return null;
  const purpose = String(raw).trim();
  if (!VALID_PURPOSES.has(purpose)) return null;
  if (purpose === "CashOut") return "CashOutRefi";
  return purpose;
}

export function normalizeOccupancy(raw) {
  if (!raw) return null;
  const occupancy = String(raw).trim().toUpperCase();
  if (!["PRIMARY", "SECOND_HOME", "INVESTMENT"].includes(occupancy)) {
    return null;
  }
  return transFormOccupancy(occupancy);
}

export function matchCountyInLoanLimits(county, state, loanLimits) {
  if (!county || !state || !loanLimits?.[state]) return county;
  const keys = Object.keys(loanLimits[state]);
  if (keys.includes(county)) return county;
  const base = county.replace(/\s+(COUNTY|PARISH)$/i, "").trim();
  const match = keys.find(
    (key) => key.replace(/\s+(COUNTY|PARISH)$/i, "").trim() === base
  );
  return match || county;
}

function stripUrlQuery() {
  if (typeof window === "undefined" || !window.location.search) return;
  const { pathname, hash } = window.location;
  window.history.replaceState(window.history.state, "", pathname + hash);
}

function hasRecognizedQueryParams(query) {
  return QUERY_KEYS.some((key) => queryHasKey(query, key));
}

/**
 * 深链 query 解析：仅返回 URL 中出现的字段（稀疏对象），键名与 URL 一致。
 * 无相关 query 时返回 null，调用方走表单原有默认值。
 * 读取后立即清除 URL query。
 */
export function parseSearchFieldsFromUrlQuery() {
  if (typeof window === "undefined") return null;

  const query = new URLSearchParams(window.location.search);
  if (!hasRecognizedQueryParams(query)) return null;

  const fields = {};

  if (queryHasKey(query, "fico")) {
    const fico = parseIntOrNull(query.get("fico"), { min: 300, max: 850 });
    if (fico != null) fields.fico = fico;
  }

  if (queryHasKey(query, "loanAmount")) {
    const loanAmount = parseIntOrNull(query.get("loanAmount"), {
      min: 1,
      max: 100_000_000,
    });
    if (loanAmount != null) fields.loanAmount = loanAmount;
  }

  if (queryHasKey(query, "state")) {
    const state = normalizeState(query.get("state"));
    if (state) fields.state = state;
  }

  if (queryHasKey(query, "county")) {
    const county = normalizeCounty(query.get("county"));
    if (county) fields.county = county;
  }

  if (queryHasKey(query, "housePrice")) {
    const housePrice = parseIntOrNull(query.get("housePrice"), {
      min: 1,
      max: 100_000_000,
    });
    if (housePrice != null) fields.housePrice = housePrice;
  }

  if (queryHasKey(query, "purpose")) {
    const purpose = normalizePurpose(query.get("purpose"));
    if (purpose) fields.purpose = purpose;
  }

  if (queryHasKey(query, "occupancy")) {
    const occupancy = normalizeOccupancy(query.get("occupancy"));
    if (occupancy) fields.occupancy = occupancy;
  }

  if (queryHasKey(query, "monthlyIncome")) {
    const monthlyIncome = parseIntOrNull(query.get("monthlyIncome"), {
      min: 0,
      max: 10_000_000,
    });
    if (monthlyIncome != null) fields.monthlyIncome = monthlyIncome;
  }

  if (queryHasKey(query, "monthlyDebt")) {
    const monthlyDebt = parseIntOrNull(query.get("monthlyDebt"), {
      min: 0,
      max: 10_000_000,
    });
    if (monthlyDebt != null) fields.monthlyDebt = monthlyDebt;
  }

  stripUrlQuery();
  return { fromDeepLink: true, ...fields };
}
